import * as THREE from 'three';

/**
 * Client netcode: prediction, reconciliation and interpolation.
 *
 * The server is authoritative, but waiting for it to answer before you move
 * would put your own input a full round trip behind your hands, which is
 * unplayable at any latency worth caring about. So:
 *
 *   - YOUR player is simulated locally the instant you press a key, and every
 *     command is kept until the server acknowledges it.
 *   - When a snapshot arrives it is the truth as of some input you sent a while
 *     ago. The local player is snapped to that state and every command the
 *     server has not yet seen is REPLAYED on top. If the server agreed with the
 *     prediction — which it does, because both sides run the same
 *     PlayerController — you end up exactly where you already were and nothing
 *     visibly happens. Divergence shows up as a small correction rather than a
 *     teleport.
 *   - OTHER players are drawn from an interpolation buffer, deliberately ~100 ms
 *     in the past. Snapshots arrive 20 times a second; rendering them the moment
 *     they land would make everyone else move in 50 ms steps. Rendering slightly
 *     behind lets us always have two snapshots to blend between, which is what
 *     makes other players move smoothly.
 */

/** How far behind live to render other players. */
export const INTERP_DELAY_MS = 100;
/** Corrections smaller than this are ignored — see reconcile(). */
const SNAP_EPSILON = 0.02;
/** How many recent commands ride along with each send. See sendCommand(). */
const REDUNDANCY = 3;
/** A correction bigger than this is a teleport (spawn, respawn); do not smooth. */
const HARD_SNAP = 3.0;

const _v = new THREE.Vector3();
const _before = new THREE.Vector3();
/** Shared empty result, so the common "nothing happened" path allocates nothing. */
const EMPTY = [];

export class NetClient {
  /**
   * @param {object} opts
   * @param {string} opts.url        wss://host:port/
   * @param {string} opts.password
   * @param {string} opts.map
   * @param {string} opts.mode       'tdm' | 'bomb'
   * @param {string} [opts.team]
   * @param {string} [opts.name]
   */
  constructor(opts) {
    this.opts = opts;
    this.ws = null;
    this.state = 'idle';          // idle | connecting | live | error | closed
    this.error = null;
    this.selfId = null;
    this.team = null;
    this.tickHz = 30;
    this.snapHz = 20;

    /** Commands sent but not yet acknowledged, oldest first. */
    this.pending = [];
    this.seq = 0;

    /** id -> { name, team, buffer: [{t, pos, yaw, crouch}], health, alive, kills, deaths } */
    this.remotes = new Map();
    this.score = { red: 0, blue: 0 };

    this.ping = 0;
    this._pingSent = 0;
    /** Offset between server clock and ours, so buffer timestamps are comparable. */
    this._clockOffset = 0;

    /* Diagnostics — what a probe asserts on and what a HUD can display. */
    this.stats = {
      snapshots: 0, corrections: 0, replayed: 0,
      lastError: 0, maxError: 0, hardSnaps: 0,
    };

    this.onWelcome = null;
    this.onClose = null;

    /** Events not yet handled by the game: kills, hits, respawns. */
    this._newEvents = [];
  }

  /** Take everything that has happened since the last call. */
  drainEvents() {
    if (!this._newEvents.length) return EMPTY;
    const out = this._newEvents;
    this._newEvents = [];
    return out;
  }

  /**
   * The public room list. No password: you have to be able to SEE what is
   * running before you can sensibly ask for the password to one of them.
   */
  static async listRooms(baseUrl, master = '') {
    const http = baseUrl.replace(/^ws/, 'http').replace(/\/$/, '');
    const res = await fetch(`${http}/rooms?p=${encodeURIComponent(master)}`, { cache: 'no-store' });
    // 401 is the master password being wrong, and it is worth distinguishing:
    // "the server refused you" and "the server is not there" need different
    // things from the player.
    if (res.status === 401) throw new Error('bad-master');
    if (res.status === 429) throw new Error('throttled');
    if (!res.ok) throw new Error(`server said ${res.status}`);
    return (await res.json()).rooms ?? [];
  }

  connect(WebSocketImpl = globalThis.WebSocket) {
    const o = this.opts;
    // `m` is the master password (the server), `p` the room's own (optional).
    const q = new URLSearchParams({
      m: o.master ?? '', p: o.password ?? '', name: o.name ?? '',
    });
    // The room is the PATH. One address, many rooms, each with its own secret.
    const url = `${o.url.replace(/\/$/, '')}/r/${o.roomId}?${q}`;

    this.state = 'connecting';
    return new Promise((resolve, reject) => {
      let ws;
      try { ws = new WebSocketImpl(url); } catch (e) { this.state = 'error'; return reject(e); }
      this.ws = ws;

      const fail = (reason) => {
        this.state = 'error';
        this.error = reason;
        reject(new Error(reason));
      };

      // A rejected upgrade surfaces differently in a browser (a bare close,
      // because the spec hides the HTTP status from script) than under ws in
      // Node (an explicit unexpected-response). Both have to be handled or a
      // wrong password looks like a hang.
      ws.onerror = () => { if (this.state === 'connecting') fail('unreachable'); };
      ws.onclose = (e) => {
        if (this.state === 'connecting') {
          fail(e && e.code === 4003 ? 'full'
            : e && e.code === 4004 ? 'room-closed' : 'refused');
          // A browser cannot see the HTTP status behind a rejected upgrade, so
          // 401 (master) and 403 (room) are indistinguishable here. Node can
          // tell them apart — see the unexpected-response handler below.
        } else {
          this.state = 'closed';
          this.onClose?.(e);
        }
      };
      ws.on?.('unexpected-response', (_req, res) => {
        fail(res.statusCode === 401 ? 'bad-master'
          : res.statusCode === 403 ? 'bad-password'
            : res.statusCode === 429 ? 'throttled' : `http-${res.statusCode}`);
      });

      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : ev.data.toString()); }
        catch { return; }
        if (msg.type === 'welcome') {
          this.selfId = msg.id;
          this.serverMap = msg.map;
          this.roomInfo = msg.room ?? null;
          this.serverMode = msg.mode;
          this.maxPlayers = msg.max ?? 12;
          this.maxHealth = msg.maxHealth ?? 100;
          this.tickHz = msg.tickHz ?? 30;
          this.snapHz = msg.snapHz ?? 20;
          this.state = 'live';
          // Kept so the game can place the local player exactly where the
          // server did rather than wherever the level's own spawn logic chose.
          this._welcomeSpawn = msg.spawn;
          this.onWelcome?.(msg);
          resolve(msg);
        } else if (msg.type === 'roster') {
          // Who is here, on which side, and how full the server is. Pushed on
          // every change so the lobby can never show a stale count.
          this.roster = msg;
          this.onRoster?.(msg);
        } else if (msg.type === 'spawned') {
          this.team = msg.team;
          this._welcomeSpawn = msg.spawn;
          this.onSpawned?.(msg);
        } else if (msg.type === 'flashed') {
          // Private: how badly the last bang caught YOU. Nobody else is told.
          this.onFlashed?.(msg.k ?? 0);
        } else if (msg.type === 'streaks') {
          // What this player currently holds, straight from the ledger.
          this.held = msg.held ?? [];
          this.onStreaks?.(this.held);
        } else if (msg.type === 'switch') {
          // Acknowledged, not applied: the side change happens at the next
          // round start. Held so the menu can say so.
          this.pendingTeam = msg.team ?? null;
          this.onSwitch?.(this.pendingTeam);
        } else if (msg.type === 'snap') {
          this._onSnapshot(msg);
        } else if (msg.type === 'pong') {
          this.ping = Date.now() - msg.t;
        } else if (msg.type === 'error') {
          if (this.state === 'live') {
            // Kicked mid-game — the admin closed this room.
            this.error = msg.reason;
            this.onKicked?.(msg.reason);
            this.close();
          } else fail(msg.reason ?? 'error');
        }
      };
    });
  }

  /**
   * Commit to a side. Only valid from the lobby — the point of the lobby is
   * that you see who is already on each team before deciding.
   */
  joinTeam(team) {
    return new Promise((resolve, reject) => {
      if (this.state !== 'live') return reject(new Error('not connected'));
      const t = setTimeout(() => reject(new Error('join timed out')), 10000);
      this.onSpawned = (msg) => { clearTimeout(t); this.onSpawned = null; resolve(msg); };
      try { this.ws.send(JSON.stringify({ type: 'join', team })); }
      catch (e) { clearTimeout(t); reject(e); }
    });
  }

  /**
   * Ask the server to press the button you are standing at.
   *
   * The server decides whether one is in reach and what it opens; this is a
   * request, not a claim. Fire and forget — a lost press costs one more tap.
   */
  pressButton() {
    if (this.state !== 'live') return;
    try { this.ws.send(JSON.stringify({ type: 'button' })); } catch { /* closing */ }
  }

  /**
   * Tell the server which operator you are wearing.
   *
   * Fire and forget: it is cosmetic, there is nothing to wait for, and a lost
   * message costs somebody a different-looking hat for one match.
   */
  setOperator(op) {
    if (this.state !== 'live') return;
    try { this.ws.send(JSON.stringify({ type: 'operator', op })); } catch { /* closing */ }
  }

  /**
   * Ask to change sides at the next round. Pass your CURRENT team to cancel a
   * pending change.
   */
  requestSwitch(team) {
    if (this.state !== 'live') return;
    try { this.ws.send(JSON.stringify({ type: 'switch', team })); } catch { /* closing */ }
  }

  close() {
    try { this.ws?.close(); } catch { /* already gone */ }
    this.state = 'closed';
  }

  /* ------------------------------------------------------------ outgoing -- */

  /**
   * Send one input frame and predict it locally.
   *
   * @param {object} cmd  { keys, pressed, yaw, pitch, fire, ads }
   * @param {number} dt   the timestep this command represents
   * @param {object} pc   the local PlayerController
   * @param {object} input the NetInput-shaped adapter driving `pc`
   */
  sendCommand(cmd, dt, pc, input) {
    if (this.state !== 'live') return;
    const full = { type: 'cmd', seq: ++this.seq, dt, ...cmd };
    this.pending.push(full);
    // A backlog this long means the connection is gone; dropping the oldest
    // keeps replay bounded rather than letting it grow without limit.
    if (this.pending.length > 200) this.pending.shift();

    // Input redundancy: resend the last few unacknowledged commands alongside
    // the new one.
    //
    // A dropped command is not a dropped frame, it is a divergence — the client
    // predicted an input the server will never simulate, and the difference
    // survives until something corrects it. TCP will eventually retransmit, but
    // "eventually" is the head-of-line stall we are already trying to avoid.
    // Carrying two extra copies costs a few dozen bytes and makes a single loss
    // a non-event. The server already ignores anything at or below the sequence
    // it has consumed, so duplicates are free.
    const redundant = this.pending.slice(-REDUNDANCY, -1);
    const payload = redundant.length
      ? JSON.stringify({ type: 'cmds', c: [...redundant, full] })
      : JSON.stringify(full);
    try { this.ws.send(payload); } catch { /* closing */ }

    // Predict immediately — this is what makes your own movement feel local.
    input.apply(full);
    pc.yaw = full.yaw ?? pc.yaw;
    pc.pitch = full.pitch ?? pc.pitch;
    pc.update(dt, { frozen: this.inputLocked });
    input.endFrame();
  }

  /**
   * Is movement locked right now?
   *
   * Prediction has to apply the SAME rules the server applies or it diverges by
   * construction. Freeze time is the obvious trap: the server refuses to move
   * anyone, the client happily predicts a sprint, and every snapshot arrives as
   * a correction. Measured, that was a constant 147 mm error at zero latency —
   * small enough to look like a netcode quirk rather than a missing rule.
   */
  get inputLocked() {
    const p = this.round?.phase;
    return p === 'freeze' || p === 'over';
  }

  ping_() {
    if (this.state !== 'live') return;
    this._pingSent = Date.now();
    try { this.ws.send(JSON.stringify({ type: 'ping', t: this._pingSent })); } catch { /* closing */ }
  }

  /* ------------------------------------------------------------ incoming -- */

  _onSnapshot(snap) {
    this.stats.snapshots++;
    this.score = snap.score ?? this.score;
    /** Authoritative round state: phase, clock, bomb, plant/defuse progress. */
    if (snap.round) this.round = snap.round;
    if (snap.ev) {
      this.events = (this.events ?? []).concat(snap.ev).slice(-32);
      // A separate queue the game consumes exactly once. `events` is a rolling
      // log for debugging; reacting to it would re-fire every hitmarker on
      // every frame.
      this._newEvents.push(...snap.ev);
    }
    const now = Date.now();

    for (const e of snap.players) {
      if (e.i === this.selfId) {
        this._self = e;
        // Your own score comes from the server too — the local HUD counters
        // only know about hits this client thinks it landed.
        this.selfKills = e.k;
        this.selfDeaths = e.d;
        // And so does your health. `reconcile` deliberately only adopts the
        // fields that feed movement, so health was arriving in every snapshot
        // and being dropped on the floor: you could be shot to death without
        // the bar ever moving. Kept OUTSIDE `_self` because `reconcile` clears
        // that after one use and the HUD reads every frame.
        this.selfHealth = e.h;
        if (e.mh) this.maxHealth = e.mh;
        this.selfAlive = !!e.a;
        // And your side, which is not fixed for the match: a queued switch
        // takes effect at a round start, and everything that colours the world
        // — the reticle, the scoreboard, who counts as a teammate — reads this.
        if (e.t && e.t !== this.team) { this.team = e.t; this.onTeamChange?.(e.t); }
        continue;
      }
      let r = this.remotes.get(e.i);
      if (!r) {
        r = { id: e.i, name: e.n, team: e.t, op: e.o ?? 0, buffer: [], health: 100, alive: true, kills: 0, deaths: 0 };
        this.remotes.set(e.i, r);
      }
      // The operator has to be here BEFORE the body is built: `syncBodies`
      // reads it exactly once, when it first sees this player, and a rig
      // already in the world does not change its kit.
      r.name = e.n; r.team = e.t; r.op = e.o ?? 0;
      r.health = e.h; r.alive = !!e.a; r.kills = e.k; r.deaths = e.d;
      r.maxHealth = e.mh ?? this.maxHealth;
      // Timestamped with the SERVER's clock; sampling compares like with like.
      // `ch` is the capsule height, and it rides along because the eye-to-feet
      // offset depends on it — a crouching player interpolated with the standing
      // offset stands up to their ankles in the floor.
      r.buffer.push({ t: snap.t, pos: e.p, yaw: e.y, crouch: !!e.c, ch: e.ch });
      // Keep a second of history — far more than the interpolation delay needs,
      // but enough to ride out a gap without running dry.
      const cutoff = snap.t - 1000;
      while (r.buffer.length > 2 && r.buffer[0].t < cutoff) r.buffer.shift();
    }

    // Anyone absent from the snapshot has left.
    const present = new Set(snap.players.map((p) => p.i));
    for (const id of this.remotes.keys()) if (!present.has(id)) this.remotes.delete(id);

    this._lastSnapAt = now;
    this._serverTime = snap.t;
    // Offset between the server's clock and ours, so "now, on the server" can be
    // computed CONTINUOUSLY rather than only when a packet lands.
    //
    // Sampling against the last snapshot's timestamp looks equivalent and is
    // not: that value only advances 20 times a second, so the interpolation
    // target is a staircase and every remote player moves in 50 ms hops. It
    // measured as a p95 jerk of 15 m/s — the exact stutter interpolation exists
    // to remove. Smoothed rather than assigned, so one late packet does not
    // yank the clock.
    const offset = snap.t - now;
    this._clockOffset = this._clockOffset === 0 ? offset
      : this._clockOffset + (offset - this._clockOffset) * 0.1;
  }

  /**
   * Apply the server's view of US, then replay whatever it has not seen yet.
   * Call once per frame, after any snapshots have arrived.
   */
  reconcile(pc, input) {
    const e = this._self;
    if (!e || this.state !== 'live') return 0;
    this._self = null;

    // Drop everything the server has already folded in.
    while (this.pending.length && this.pending[0].seq <= e.s) this.pending.shift();

    // The number that matters is the VISIBLE correction: how far the player
    // gets moved by reconciling, not how far the snapshot's position is from
    // where we are now.
    //
    // Those are very different things and confusing them is easy. A snapshot
    // describes the world as of an input we sent a round trip ago; we have
    // predicted several frames past it since. Comparing directly measures the
    // distance travelled during the unacknowledged window — at 7 m/s and 150 ms
    // that is a metre, and it is not an error at all. The correction is the
    // difference between where we were and where we end up AFTER replaying.
    _before.copy(pc.position);

    _v.set(e.p[0], e.p[1], e.p[2]);
    const rawGap = pc.position.distanceTo(_v);
    // Nothing to do if the server's own state already matches where we thought
    // we were at that point; below the epsilon a correction is pure jitter.
    if (rawGap < SNAP_EPSILON && this.pending.length === 0) {
      this.stats.lastError = 0;
      return 0;
    }

    // Adopt the server's state wholesale. Position alone is not enough:
    // velocity and stance feed straight back into the next step through
    // acceleration, friction and capsule height.
    // `e.p` is the EYE position (capsule.end), not the feet. teleport() takes
    // feet, so calling it with the eye position silently places the player a
    // capsule-height out — worth 1.0 m of phantom correction until it is caught.
    // Setting both ends explicitly leaves no room for that confusion.
    pc.capsule.end.set(e.p[0], e.p[1], e.p[2]);
    pc.capsule.start.set(e.p[0], e.p[1] - (pc.height - pc.capsule.radius * 2), e.p[2]);
    if (e.v) pc.velocity.set(e.v[0], e.v[1], e.v[2]);
    if (typeof e.ch === 'number') pc.height = e.ch;
    pc.grounded = !!e.g;

    // Replay the unacknowledged tail so the player ends up where their most
    // recent input should have put them, not where they were N frames ago.
    for (const cmd of this.pending) {
      input.apply(cmd);
      pc.yaw = cmd.yaw ?? pc.yaw;
      pc.pitch = cmd.pitch ?? pc.pitch;
      pc.update(cmd.dt, { frozen: this.inputLocked });
      input.endFrame();
      this.stats.replayed++;
    }

    const correction = pc.position.distanceTo(_before);
    this.stats.lastError = correction;
    if (correction > this.stats.maxError) this.stats.maxError = correction;
    if (correction > SNAP_EPSILON) this.stats.corrections++;
    if (correction > HARD_SNAP) this.stats.hardSnaps++;
    return correction;
  }

  /**
   * Where a remote player should be drawn right now: a blend between the two
   * snapshots straddling (now - INTERP_DELAY_MS).
   */
  /** Server time as of right now, advancing between snapshots. */
  serverNow() { return Date.now() + this._clockOffset; }

  sampleRemote(r, out = new THREE.Vector3(), nowServer = this.serverNow()) {
    const buf = r.buffer;
    if (!buf.length) return null;
    const target = (nowServer ?? Date.now()) - INTERP_DELAY_MS;

    if (buf.length === 1 || target <= buf[0].t) {
      const b = buf[0];
      out.set(b.pos[0], b.pos[1], b.pos[2]);
      return { pos: out, yaw: b.yaw, crouch: b.crouch, ch: b.ch, extrapolated: false };
    }
    for (let i = 0; i < buf.length - 1; i++) {
      const a = buf[i], b = buf[i + 1];
      if (target >= a.t && target <= b.t) {
        const span = b.t - a.t;
        const f = span > 0 ? (target - a.t) / span : 0;
        out.set(
          a.pos[0] + (b.pos[0] - a.pos[0]) * f,
          a.pos[1] + (b.pos[1] - a.pos[1]) * f,
          a.pos[2] + (b.pos[2] - a.pos[2]) * f,
        );
        return {
          pos: out,
          yaw: lerpAngle(a.yaw, b.yaw, f),
          crouch: b.crouch,
          ch: (a.ch ?? b.ch) + ((b.ch ?? a.ch) - (a.ch ?? b.ch)) * f,
          extrapolated: false,
        };
      }
    }
    // Ahead of the newest sample: hold the last known pose rather than
    // extrapolating. A guess that turns out wrong reads as a player skating
    // into a wall and then snapping back, which is worse than a brief stall.
    const b = buf[buf.length - 1];
    out.set(b.pos[0], b.pos[1], b.pos[2]);
    return { pos: out, yaw: b.yaw, crouch: b.crouch, ch: b.ch, extrapolated: true };
  }
}

/** Shortest-way angular interpolation, so crossing PI does not spin the model. */
function lerpAngle(a, b, f) {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * f;
}
